// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: reads the stores for the Konvo panel's quick tasks (planQuickTasks): what the page
// under the panel has open (the Konvo, Teams or WhatsApp chat, the email in the reader, the
// ClickUp task) and the conversations, mail and ClickUp updates with recent activity, in the
// same shape Home ranks. Called from an observer, so it re-runs as messages come in.

import ChannelPreviews from '@app/features/channel/state/ChannelPreviews';
import Channels from '@app/features/channel/state/Channels';
import {pickNewerMessage} from '@app/features/channel/utils/ChannelPreviewModel';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import {mailSenderName} from '@app/features/channel/utils/ChatsPanelModel';
import Agents from '@app/features/konvo_agents/state/Agents';
import {parseAgentsPath} from '@app/features/konvo_agents/state/AgentsModel';
import KonvoTeamsGroups from '@app/features/konvo_connections/state/KonvoTeamsGroups';
import {pageForPath} from '@app/features/konvo_corner/state/CornerQueue';
import KonvoMail from '@app/features/konvo_mail/state/KonvoMail';
import ClickUpInbox from '@app/features/konvo_notifications/state/KonvoClickUpInbox';
import PanelFocus from '@app/features/konvo_panel/state/PanelFocus';
import type {
	AgentFocus,
	ChatFocus,
	QuickTaskInput,
	RecentItem,
	TaskFocus,
} from '@app/features/konvo_panel/state/quickTasks';
import KonvoTasks from '@app/features/konvo_tasks/state/KonvoTasks';
import KonvoWhatsApp from '@app/features/konvo_whatsapp/state/KonvoWhatsApp';
import Messages from '@app/features/messaging/state/MessagingMessages';
import {getSortedDmChannels} from '@app/features/messaging/utils/DmChannelUtils';
import {messagePreviewText} from '@app/features/messaging/utils/MessagePreviewText';
import Navigation from '@app/features/navigation/state/Navigation';
import ReadStates from '@app/features/read_state/state/ReadStates';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import Users from '@app/features/user/state/Users';
import {KONVOBOT_ID} from '@konvo/constants/src/AppConstants';
import {ChannelTypes} from '@konvo/constants/src/ChannelConstants';
import {isAutomatedMail, isNewsletterMail, isSecurityMail} from '@konvo/schema/src/domains/konvo/HomeFeedModel';
import {extractTimestamp} from '@konvo/snowflake/src/SnowflakeUtils';

/** Enough to find the last hour's activity without walking every chat. */
const MAX_DMS = 40;
const MAX_MAIL = 30;

function parseIsoMs(value: string | null | undefined): number | null {
	if (!value) return null;
	const ms = Date.parse(value);
	return Number.isFinite(ms) ? ms : null;
}

/** "Sarah: are we still on?" → Sarah (groups put the sender first; "You:" is the user). */
function previewSender(preview: string): string | undefined {
	const match = /^([^:]{1,40}):\s/.exec(preview);
	if (!match || match[1] === 'You') return undefined;
	return match[1];
}

export function whatsappChatFocus(jid: string): ChatFocus | null {
	const chat = KonvoWhatsApp.chats.find((entry) => entry.jid === jid);
	if (!chat) return null;
	const preview = (chat.last_preview ?? '').trim();
	return {
		source: 'whatsapp',
		conversationId: `whatsapp:${chat.jid}`,
		name: chat.name,
		group: chat.is_group,
		unread: chat.unread,
		lastFromThem: preview !== '' && !/^You:\s/.test(preview),
		lastSender: chat.is_group ? previewSender(preview) : undefined,
	};
}

export function konvoChatFocus(channelId: string): ChatFocus | null {
	const channel = Channels.getChannel(channelId);
	if (!channel || channel.recipientIds.includes(KONVOBOT_ID)) return null;
	const me = Users.getCurrentUser()?.id;
	const teamsLink = KonvoTeamsGroups.linkFor(channel.id);
	const isDm = channel.type === ChannelTypes.DM || channel.type === ChannelTypes.GROUP_DM;
	const recipient = channel.type === ChannelTypes.DM ? Users.getUser(channel.recipientIds[0] ?? '') : null;
	const teams = teamsLink != null || (recipient?.isTeamsShadow() ?? false);
	const last = pickNewerMessage(Messages.getCachedMessages(channel.id)?.last(), ChannelPreviews.get(channel.id));
	const lastFromThem = last != null && last.author.id !== me && last.author.id !== KONVOBOT_ID;
	return {
		source: teams ? 'teams' : 'konvo',
		conversationId: teamsLink?.chat_id ? `teams:${encodeURIComponent(teamsLink.chat_id)}` : `konvo:${channel.id}`,
		name: isDm ? ChannelUtils.getDMDisplayName(channel) : `#${channel.name ?? ''}`,
		group: channel.type !== ChannelTypes.DM,
		unread: isDm ? ReadStates.getPrivateChannelUnreadCount(channel.id) : ReadStates.getUnreadCount(channel.id),
		lastFromThem,
		lastSender: lastFromThem ? last.author.displayName : undefined,
	};
}

/** The chat the page shows: a WhatsApp chat, or a Konvo DM, group or channel. */
function chatFocus(path: string): ChatFocus | null {
	if (path.includes('/whatsapp')) {
		const jid = KonvoWhatsApp.selectedJid;
		return jid ? whatsappChatFocus(jid) : null;
	}
	const match = /^\/channels\/(@me|\d+)\/(\d+)/.exec(path);
	return match ? konvoChatFocus(match[2]!) : null;
}

function agentFocus(path: string): AgentFocus | null {
	const {machineId, threadId} = parseAgentsPath(path);
	if (!machineId || !threadId) return null;
	const title =
		Agents.summary(machineId, threadId)?.title ?? Agents.thread(machineId, threadId)?.detail?.title ?? threadId;
	return {machineId, threadId, title};
}

function taskFocus(page: string): TaskFocus | null {
	if (page !== 'tasks' || !KonvoTasks.openTaskId) return null;
	const task = KonvoTasks.getTask(KonvoTasks.openTaskId);
	return task?.id ? {taskId: task.id, name: task.name} : null;
}

function recentDms(): Array<RecentItem> {
	const me = Users.getCurrentUser();
	const out: Array<RecentItem> = [];
	for (const channel of getSortedDmChannels(Channels.dmChannels, me?.id).slice(0, MAX_DMS)) {
		if (channel.recipientIds.includes(KONVOBOT_ID)) continue;
		const recipient = channel.type === ChannelTypes.DM ? Users.getUser(channel.recipientIds[0] ?? '') : null;
		const teamsLink = KonvoTeamsGroups.linkFor(channel.id);
		const teams = teamsLink != null || (recipient?.isTeamsShadow() ?? false);
		const last = pickNewerMessage(Messages.getCachedMessages(channel.id)?.last(), ChannelPreviews.get(channel.id));
		const hasUnread = ReadStates.hasUnread(channel.id);
		out.push({
			key: `channel:${channel.id}`,
			source: teams ? 'teams' : 'konvo',
			name: ChannelUtils.getDMDisplayName(channel),
			preview: last ? messagePreviewText(last.content) : '',
			lastAt: last ? extractTimestamp(last.id) : channel.lastMessageId ? extractTimestamp(channel.lastMessageId) : null,
			unread: hasUnread ? Math.max(1, ReadStates.getPrivateChannelUnreadCount(channel.id)) : 0,
			mention: ReadStates.getPrivateChannelMentionCount(channel.id) > 0,
			muted: UserGuildSettings.isChannelDirectlyMuted(null, channel.id),
			group: channel.type === ChannelTypes.GROUP_DM,
			fromMe: last != null && last.author.id === me?.id,
			conversationId: teamsLink?.chat_id ? `teams:${encodeURIComponent(teamsLink.chat_id)}` : `konvo:${channel.id}`,
		});
	}
	return out;
}

function recentWhatsApp(now: number): Array<RecentItem> {
	if (!KonvoWhatsApp.connected) return [];
	return KonvoWhatsApp.chats
		.filter((chat) => !chat.archived)
		.map((chat) => {
			const preview = (chat.last_preview ?? '').replace(/\s+/g, ' ').trim();
			return {
				key: `whatsapp:${chat.jid}`,
				source: 'whatsapp' as const,
				name: chat.name,
				preview,
				lastAt: parseIsoMs(chat.last_at),
				unread: chat.unread,
				muted: Boolean(chat.mute_until && (chat.mute_until < 0 || chat.mute_until > now / 1000)),
				group: chat.is_group,
				fromMe: /^You:\s/.test(preview),
				conversationId: `whatsapp:${chat.jid}`,
			};
		});
}

function recentMail(): Array<RecentItem> {
	if (KonvoMail.folder !== 'inbox' || !KonvoMail.connected) return [];
	return KonvoMail.messages
		.filter((message) => !message.is_draft && (!message.is_read || message.is_flagged))
		.slice(0, MAX_MAIL)
		.map((message) => {
			const subject = message.subject.trim();
			const preview = message.preview.replace(/\s+/g, ' ').trim();
			const security = isSecurityMail({subject, preview});
			return {
				key: `mail:${message.id}`,
				source: 'mail' as const,
				name: mailSenderName(message) || message.from_address || subject,
				preview: subject || preview,
				lastAt: parseIsoMs(message.received_at),
				unread: message.is_read ? 0 : 1,
				flagged: message.is_flagged,
				security,
				newsletter: !security && isNewsletterMail({from_address: message.from_address, subject, preview}),
				automated: !security && isAutomatedMail({from_address: message.from_address, subject}),
				messageId: message.id,
			};
		});
}

function recentClickUp(): Array<RecentItem> {
	if (!ClickUpInbox.enabled) return [];
	return ClickUpInbox.activeItems.map((item) => ({
		key: `clickup:${item.id}`,
		source: 'clickup' as const,
		name: item.task_name,
		preview: '',
		lastAt: parseIsoMs(item.created_at),
		unread: item.is_read ? 0 : 1,
		read: item.is_read,
	}));
}

/** Everything planQuickTasks needs, from the stores, for the page under the panel. */
export function gatherQuickTaskInput(now: number = Date.now()): QuickTaskInput {
	void Messages.version;
	const path = Navigation.pathname;
	const page = pageForPath(path);
	const mailFocus = PanelFocus.mail;
	const onMail = page === 'mail' || path.includes('/mail');
	const mail = onMail && mailFocus ? {context: mailFocus.context, starters: mailFocus.starters} : null;
	const chat = mail ? null : chatFocus(path);
	const task = chat || mail ? null : taskFocus(page);
	const agent = chat || mail || task || page !== 'agents' ? null : agentFocus(path);
	return {
		page,
		chat,
		mail,
		task,
		agent,
		recent: [...recentDms(), ...recentWhatsApp(now), ...recentMail(), ...recentClickUp()],
		clickUp: ClickUpInbox.enabled === true,
		now,
	};
}

/** The local flow behind a mail starting point (the drafted-task form, the reply composer). */
export function quickTaskPerform(starter: string | undefined): (() => void) | undefined {
	if (!starter) return undefined;
	return PanelFocus.mail?.perform[starter as keyof typeof PanelFocus.mail.perform];
}
